// animated-list.js
// A generic Alpine component that animates list reflows when children are added.
// Works with any block element as a "list" container; each direct child is an item.

export default (opts = {}) => {
  const duration = opts.duration ?? 700 // ms
  const easing = opts.easing ?? 'ease' // CSS easing
  const fadeNew = opts.fadeNew ?? true // fade/slide-in for newly added nodes

  return {
    _observer: null,
    _prevRects: new Map(),

    init() {
      const list = this.$refs.list
      if (!list) {
        console.warn('[animatedList] Missing x-ref="list" on the container.')
        return
      }

      // Defer priming and the initial snapshot to ensure DOM is fully ready
      this.$nextTick(() => {
        this._primeChildren()
        this._snapshot()
      })

      // Observe DOM insertions
      this._observer = new MutationObserver((mutations) => {
        const added = []
        for (const m of mutations) {
          m.addedNodes.forEach(n => {
            if (n.nodeType === 1) added.push(n) // element nodes only
          })
        }
        // Play FLIP for everything that moved; give browser the new layout first
        this.$nextTick(() => this._play(added))
      })

      this._observer.observe(list, { childList: true })
    },

    // --- Public helper (optional): insert at index for demos/tools ---
    insertAt(index, el = null) {
      const list = this.$refs.list
      const kids = Array.from(list.children)
      for (let i = 0; i < 3; i++) {
        const node = el ?? this._defaultNewNode()
        const before = kids[index] ?? null // null = append
        list.insertBefore(node, before)
      }
      // MutationObserver will handle the animation
    },

    // --- internals ---
    _defaultNewNode() {
      const div = document.createElement('div')
      div.className = 'rounded-2xl bg-white shadow p-3 text-slate-800 flex items-center justify-between'
      div.innerHTML = '<span>➕ New item</span>'
      return div
    },

    _primeChildren() {
      const list = this.$refs.list
      if (!list) { return }
      const children = Array.from(list.children)
      children.forEach((el) => {
        // Prepare for transform animations without causing any visual change
        el.style.transition = 'none'
        el.style.willChange = 'transform'
        // Use a neutral transform so the browser creates a composited layer
        el.style.transform = 'translateY(0)'
      })
      // Remove the neutral inline transform and transition on next frame to avoid interference
      requestAnimationFrame(() => {
        children.forEach((el) => {
          el.style.transition = ''
          el.style.transform = ''
        })
      })
    },

    _snapshot() {
      const list = this.$refs.list
      if (!list) return
      const rects = new Map()
      Array.from(list.children).forEach(el => {
        rects.set(el, el.offsetTop)
      })
      this._prevRects = rects
    },

    _play(addedElements) {
      const list = this.$refs.list
      if (!list) return

      const children = Array.from(list.children)

      children.forEach(el => {
        const lastTop = el.offsetTop
        const firstTop = this._prevRects.get(el)

        // Newly added element
        const isNew = firstTop === undefined

        if (isNew) {
          if (fadeNew) {
            el.style.transition = 'none'
            el.style.opacity = '0'
            el.style.transform = 'translateY(-8px)'
            requestAnimationFrame(() => {
              el.style.transition = `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`
              el.style.opacity = '1'
              el.style.transform = ''
            })
          }
          return
        }

        // Existing element that moved
        const dy = firstTop - lastTop
        if (dy) {
          el.style.transition = 'none'
          el.style.transform = `translateY(${dy}px)`
          requestAnimationFrame(() => {
            el.style.transition = `transform ${duration}ms ${easing}`
            el.style.transform = ''
          })
        }
      })

      // After we've kicked off animations, refresh the snapshot so future changes animate from here
      requestAnimationFrame(() => {
        this._snapshot()
      })
    },

    destroy() {
      this._observer?.disconnect()
    }
  }
}
